import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Team.css';

const Team = () => {
  const [selectedCategory, setSelectedCategory] = useState('founders');
  const [teamMembers, setTeamMembers] = useState([]);
  const [orgChart, setOrgChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    profilePicture: null
  });
  const fileInputRef = useRef(null);
  const orgChartInputRef = useRef(null);

  const categories = [
    { value: 'founders', label: 'Founders' },
    { value: 'board', label: 'Board of Directors' },
    { value: 'advisory', label: 'Science Advisory Board' },
    { value: 'orgchart', label: 'Organization Chart' }
  ];

  useEffect(() => {
    if (selectedCategory === 'orgchart') {
      fetchOrgChart();
    } else {
      fetchTeamMembers();
    }
  }, [selectedCategory]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/team/${selectedCategory}`, {credentials: 'include'});
      if (response.ok) {
        const data = await response.json();
        console.log("team members data:", data);
        setTeamMembers(data);
      } else {
        console.error('Failed to fetch team members');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const fetchOrgChart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/orgchart`, {credentials: 'include'});
      if (response.ok) {
        const data = await response.json();
        setOrgChart(data);
      } else {
        setOrgChart(null);
      }
    } catch (error) {
      console.error('Error fetching org chart:', error);
      setOrgChart(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddMember = () => {
    setEditingMember(null);
    setFormData({ name: '', description: '', profilePicture: null });
    setShowAddModal(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      description: member.description,
      profilePicture: null
    });
    setShowAddModal(true);
  };

  const handleSaveMember = async () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append('name', formData.name);
    submitFormData.append('description', formData.description);
    submitFormData.append('category', selectedCategory);
    if (formData.profilePicture) {
      submitFormData.append('profilePicture', formData.profilePicture);
    }

    try {
      const url = editingMember 
        ? `${process.env.REACT_APP_BACKEND_API_URL}/data-room/team/${editingMember.id}`
        : `${process.env.REACT_APP_BACKEND_API_URL}/data-room/team`;
      
      const response = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        credentials: 'include',
        body: submitFormData,
      });

      if (response.ok) {
        alert(editingMember ? 'Team member updated successfully' : 'Team member added successfully');
        setShowAddModal(false);
        fetchTeamMembers();
      } else {
        const errorText = await response.text();
        alert(`Failed to save team member: ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/team/${memberId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        alert('Team member deleted successfully');
        fetchTeamMembers();
      } else {
        const errorText = await response.text();
        alert(`Failed to delete team member: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Error deleting team member');
    }
  };

  const handleOrgChartUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('orgChart', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/orgchart`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        alert('Organization chart uploaded successfully');
        fetchOrgChart();
      } else {
        const errorText = await response.text();
        alert(`Failed to upload organization chart: ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading organization chart:', error);
      alert('Error uploading organization chart');
    }
  };

  const handleViewOrgChart = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/orgchart/view`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        alert('Failed to view organization chart');
      }
    } catch (error) {
      console.error('Error viewing organization chart:', error);
      alert('Error viewing organization chart');
    }
  };

  const renderTeamMembers = () => (
    <div className="team-members-section">
      <div className="section-header">
        <h2>{categories.find(cat => cat.value === selectedCategory)?.label}</h2>
        <button className="add-member-button" onClick={handleAddMember}>
          Add Team Member
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading team members...</div>
      ) : teamMembers.length === 0 ? (
        <div className="no-members">
          <p>No team members added yet</p>
          <button className="add-member-button" onClick={handleAddMember}>
            Add First Team Member
          </button>
        </div>
      ) : (
        <div className="team-grid">
          {teamMembers.map((member) => (
            <div key={member.id} className="team-card">
              <div className="profile-picture">
                {member.profilePictureUrl ? (
                  <img 
                    src={member.profilePictureUrl} 
                    alt={member.name}
                    onError={(e) => {
                      console.error('Error loading profile picture:', e);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="placeholder-avatar"
                  style={{ display: member.profilePictureUrl ? 'none' : 'flex' }}
                >
                  {member.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="member-info">
                <h3>{member.name}</h3>
                <p>{member.description}</p>
              </div>
              <div className="member-actions">
                <button 
                  className="edit-button"
                  onClick={() => handleEditMember(member)}
                >
                  Edit
                </button>
                <button 
                  className="delete-button"
                  onClick={() => handleDeleteMember(member.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderOrgChart = () => (
    <div className="org-chart-section">
      <div className="section-header">
        <h2>Organization Chart</h2>
        <button 
          className="upload-button"
          onClick={() => orgChartInputRef.current?.click()}
        >
          Upload Organization Chart
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading organization chart...</div>
      ) : orgChart ? (
        <div className="org-chart-display">
          <div className="org-chart-info">
            <p><strong>File:</strong> {orgChart.originalName}</p>
            <p><strong>Uploaded:</strong> {new Date(orgChart.uploadedAt).toLocaleDateString()}</p>
          </div>
          <div className="org-chart-actions">
            <button className="view-button" onClick={handleViewOrgChart}>
              View Organization Chart
            </button>
            <button 
              className="replace-button"
              onClick={() => orgChartInputRef.current?.click()}
            >
              Replace Chart
            </button>
          </div>
        </div>
      ) : (
        <div className="no-org-chart">
          <p>No organization chart uploaded yet</p>
          <button 
            className="upload-button"
            onClick={() => orgChartInputRef.current?.click()}
          >
            Upload Organization Chart
          </button>
        </div>
      )}

      <input
        type="file"
        ref={orgChartInputRef}
        onChange={handleOrgChartUpload}
        accept="image/*,.pdf,.doc,.docx"
        style={{ display: 'none' }}
      />
    </div>
  );

  return (
    <div className="team-container">
      <div className="team-header">
        <h1>Team Management</h1>
        <div className="category-selector">
          <label>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCategory === 'orgchart' ? renderOrgChart() : renderTeamMembers()}

      {/* Add/Edit Member Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="team-modal">
            <h3>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</h3>
            <div className="modal-form">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter bio, role, and experience..."
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Profile Picture</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setFormData({ ...formData, profilePicture: e.target.files[0] })}
                  accept="image/*"
                />
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="save-button"
                  onClick={handleSaveMember}
                >
                  {editingMember ? 'Update' : 'Add'} Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;